import {
  LazyQueryExecFunction,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Button,
  CircularProgress,
  Grid,
  Hidden,
  Typography
} from '@material-ui/core'
import React, { useEffect } from 'react'
// eslint-disable-next-line import/no-extraneous-dependencies
import OtpInput from 'react-otp-input'
import { toast } from 'react-toastify'

import AdotelLogo from '../../assets/images/AdPoint1.png'
import BottomCurve from '../../assets/images/Vector.svg'
import ellipseImg from '../../assets/images/ellipse.svg'
import AdotelLogoForLogin from '../../assets/images/logo.svg'
import { RESEND_OTP_MUTATION, VERIFY_TWO_FA_MUTATION } from '../../graphql/auth'
import { formatTime } from '../../helpers/date'
import { getFont, getTranslate, lang } from '../../localization'
import { Credentials, UserOTPCode } from '../../types/auth'
import useStyles from './styles/verifyOTPCode.style'

type VerifyOTPCodeProps = {
  inputOTP: UserOTPCode
  setInputOTP: React.Dispatch<React.SetStateAction<UserOTPCode>>
  year: string
  timeLeftInSeconds: number
  setTimeLeftInSeconds: (
    value: number | ((prevState: number) => number)
  ) => void
  step: number
  setStep: React.Dispatch<React.SetStateAction<number>>
  getCredentials: LazyQueryExecFunction<
    {
      me: Credentials
    },
    OperationVariables
  >
}

const VerifyOTPCode = ({
  inputOTP,
  setInputOTP,
  year,
  timeLeftInSeconds,
  setTimeLeftInSeconds,
  step,
  setStep,
  getCredentials
}: VerifyOTPCodeProps) => {
  const classes = useStyles()

  const [VerifyTwoFA, { loading }] = useMutation(VERIFY_TWO_FA_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      getCredentials()
    },
    onError: (error: any) => {
      if (error.message.includes('code 401')) {
        setStep(1)
      } else {
        toast.error(error.message)
      }
    }
  })

  const [ResendOTPCode, { loading: loadingForOTPCode }] = useMutation<{
    resendOTP: { expiresIn: number }
  }>(RESEND_OTP_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (resp) => {
      setTimeLeftInSeconds(resp.resendOTP.expiresIn)
    },
    onError: (error: any) => {
      if (error.message.includes('code 401')) {
        setStep(1)
      } else {
        toast.error(error.message)
      }
    }
  })

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeftInSeconds((prev) => Math.max(prev - 1, 0))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const expired = formatTime(timeLeftInSeconds) === 'Expired'

  useEffect(() => {
    setInputOTP({ ...inputOTP, otpCode: '' })
  }, [step, expired])

  return (
    <div className={classes.root}>
      <Hidden smDown>
        <div className={classes.logoPaper}>
          <img src={AdotelLogo} alt="Adotel Co." className={classes.logoImg} />
          <div className={classes.copyrightContainer}>
            <Typography>
              ©{year} {getTranslate('Adotel')} .{' '}
              {getTranslate('All Rights Reserved')}
            </Typography>
          </div>
        </div>
      </Hidden>
      <div className={classes.loginPaper}>
        <img src={ellipseImg} alt="adotel" className={classes.ellipseIcon} />
        <div className={classes.formContainer}>
          <img
            src={AdotelLogoForLogin}
            alt="Adotel Co."
            className={classes.logoImgSmall}
          />
          <div className={classes.titleSection}>
            <Typography variant="h1" className={classes.title}>
              {getTranslate('Central Reservation System')}
            </Typography>
            <Typography variant="h4" className={classes.subtitle}>
              {getTranslate('Enter your code')}
            </Typography>
            <Typography
              variant="h5"
              className={classes.timeText}
              style={{ direction: lang == 'fa' ? 'ltr' : 'ltr' }}>
              {formatTime(timeLeftInSeconds)}
            </Typography>
          </div>
          <Grid className={classes.inputContainer}>
            <OtpInput
              value={inputOTP.otpCode}
              onChange={(value) => {
                setInputOTP({
                  ...inputOTP,
                  otpCode: value
                })
              }}
              numInputs={5}
              renderInput={(props) => <input {...props} />}
              containerStyle={{
                justifyContent: 'space-evenly',
                padding: '0px 80px'
              }}
              inputStyle={{
                width: 30,
                height: 30,
                fontFamily: getFont(),
                border: '1.5px solid black',
                borderRadius: 4
              }}
            />
          </Grid>
          <Grid className={classes.loginButtonContainer}>
            <Button
              onClick={() => {
                if (expired) {
                  ResendOTPCode()
                } else {
                  VerifyTwoFA({
                    variables: {
                      input: inputOTP
                    }
                  })
                }
              }}
              className={classes.loginButton}
              variant="contained"
              fullWidth
              size="medium"
              type="submit"
              disabled={
                (!expired && (inputOTP.otpCode.length < 5 || loading)) ||
                loadingForOTPCode
              }>
              <Typography variant="body2" className={classes.loginButtonText}>
                {getTranslate(expired ? 'Resend Code' : 'Verify Code')}
              </Typography>
              {(loading || loadingForOTPCode) && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={16}
                />
              )}
            </Button>
            <Typography
              variant="body2"
              className={classes.backButton}
              onClick={() => {
                setStep(2)
              }}>
              {getTranslate('Change Number')}
            </Typography>
          </Grid>
          <Grid className={classes.copyrightContainer}>
            <Typography>
              ©{year} {getTranslate('Adotel')} .{' '}
              {getTranslate('All Rights Reserved')}
            </Typography>
          </Grid>
        </div>
      </div>
      <img src={BottomCurve} alt="Adotel Co." className={classes.BottomCurve} />
    </div>
  )
}

export default VerifyOTPCode
